import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Pencil, RotateCcw, Check, X } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import ProgramHistoryList from "@/components/ProgramHistoryList";

const axis = { stroke: "#444", fontSize: 10, fontFamily: "monospace" };

function PBRow({ uid, pb, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(pb.value || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!val.trim()) return;
    setBusy(true);
    try {
      await api.put(`/admin/athletes/${uid}/personal-bests/${pb.key}`, { value: val.trim() });
      toast.success(`${pb.label} updated`);
      setEditing(false);
      await onChanged();
    } catch { toast.error("Could not save PB"); }
    setBusy(false);
  };
  const reset = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/athletes/${uid}/personal-bests/${pb.key}`);
      toast.success(`${pb.label} reset to auto`);
      await onChanged();
    } catch { toast.error("Could not reset"); }
    setBusy(false);
  };

  return (
    <div className="bg-[#0e0e0e] border border-[#1f1f1f] p-2.5" data-testid={`admin-pb-${pb.key}`}>
      <div className="font-mono-jp text-[8px] uppercase tracking-widest text-[#666]">{pb.label}</div>
      {editing ? (
        <div className="mt-1 flex items-center gap-1">
          <input value={val} onChange={e => setVal(e.target.value)} placeholder="e.g. 42:30 or 12 km"
            className="flex-1 min-w-0 bg-[#0a0a0a] border border-[#333] focus:border-[#d7ff00] outline-none px-2 py-1 text-white text-sm" data-testid={`admin-pb-input-${pb.key}`} />
          <button onClick={save} disabled={busy} className="text-[#d7ff00] p-1" data-testid={`admin-pb-save-${pb.key}`}><Check className="w-4 h-4" /></button>
          <button onClick={() => { setEditing(false); setVal(pb.value || ""); }} className="text-[#888] p-1"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <div className="mt-1 flex items-center justify-between gap-2">
          <div>
            <div className="font-display text-lg text-white">{pb.value || "—"}</div>
            <div className="font-mono-jp text-[8px] uppercase tracking-widest text-[#555]">{pb.value ? (pb.source === "manual" ? "coach set" : "auto") : "not set"}</div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => setEditing(true)} className="text-[#71c4ff] p-1" data-testid={`admin-pb-edit-${pb.key}`}><Pencil className="w-3.5 h-3.5" /></button>
            {pb.source === "manual" && <button onClick={reset} disabled={busy} className="text-[#888] hover:text-[#ff6b6b] p-1" title="Reset to auto" data-testid={`admin-pb-reset-${pb.key}`}><RotateCcw className="w-3.5 h-3.5" /></button>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AthleteProgramsPanel({ uid }) {
  const [pbs, setPbs] = useState([]);
  const [prog, setProg] = useState(null);

  const loadPbs = async () => {
    try { const { data } = await api.get(`/admin/athletes/${uid}/personal-bests`); setPbs(data.bests || []); } catch { /* noop */ }
  };
  useEffect(() => {
    loadPbs();
    api.get(`/admin/athletes/${uid}/progress`).then(r => setProg(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  return (
    <div className="space-y-4" data-testid={`athlete-programs-panel-${uid}`}>
      <div>
        <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] mb-2">PROGRAMS · HISTORY</div>
        <ProgramHistoryList
          listEndpoint={`/admin/athletes/${uid}/programs`}
          detailEndpoint={(pid) => `/admin/athletes/${uid}/programs/${pid}`}
          testidPrefix="admin-programs"
        />
      </div>

      <div>
        <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] mb-2 flex items-center gap-1"><Trophy className="w-3 h-3" /> PERSONAL BESTS</div>
        <div className="grid grid-cols-2 gap-2">
          {pbs.map(pb => <PBRow key={pb.key} uid={uid} pb={pb} onChanged={loadPbs} />)}
        </div>
      </div>

      <div>
        <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] mb-2">WEEKLY MILEAGE · ALL HISTORY</div>
        <div className="bg-[#0e0e0e] border border-[#1f1f1f] p-3" style={{ width: "100%", height: 180 }}>
          <ResponsiveContainer>
            <BarChart data={prog?.weekly_mileage || []}>
              <CartesianGrid stroke="#181818" vertical={false} />
              <XAxis dataKey="week" tick={axis} hide />
              <YAxis tick={axis} width={28} />
              <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 11 }} />
              <Bar dataKey="km" fill="#d7ff00" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
